import React from "react";
import { Navbar, Group, Text, Divider, Button, Avatar } from "@mantine/core";
import { NavLink, useNavigate, useMatch } from "react-router-dom";
import { useStyles } from "../../theme/them";
import AtbaImage from "../../images/atba.png";
import { Search, UserPlus, Check, AlertTriangle, Logout } from "tabler-icons-react";
import { useKeycloak } from "../../keycloak/KeycloakContext";

const data = [
  {
    link: "/search",
    label: "البحث عن شخص",
    icon: <Search size={20} />,
  },
  { 
    link: "/add", 
    label: "اضافة شخص",
    icon: <UserPlus size={20} />, 
  },
  { 
    link: "/found", 
    label: "تم ايجادهم",
    icon: <Check size={20} />, 
  },
  { 
    link: "/lost", 
    label: "المفقودين",
    icon: <AlertTriangle size={20} />, 
  },
];

const logoIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 512 512"
    style={{
      enableBackground: "new 0 0 512 512",
      width: "45px",
      height: "45px",
      transition: "transform 0.3s ease",
    }}
    fill="rgb(42, 155, 73)"
  >
    <g>
      <path
        className="st0"
        d="M99.06,426.02v26.16h366.21v52.32H46.74v-78.47c0-43.34,35.13-78.47,78.47-78.47h287.73V242.92
		c0-46.14-44.47-93.17-86.06-127.1c-28.46-23.2-55.56-40.25-66.18-46.66c-0.05-0.05-0.13-0.08-0.18-0.1
		c-2.91-1.78-4.53-2.69-4.53-2.69S101.67,130.44,46.74,242.92c0-114.21,205.99-233.54,209.24-235.39C256,7.5,256,7.5,256,7.5
		c0.1,0.05,0.24,0.1,0.34,0.18c4.71,2.74,57.83,33.85,109.31,78.53c50.59,43.84,99.61,100.79,99.61,156.71v156.95H125.21
		C110.77,399.87,99.06,411.58,99.06,426.02z"
      />
    </g>
  </svg>
);

const NavBar = () => {
  const navigate = useNavigate();
  const { classes, cx } = useStyles();
  const { userInfo, logout } = useKeycloak();
  const displayName = userInfo?.preferred_username || userInfo?.name || "المستخدم";

  const NavLinkView = ({ item }) => {
    let match = useMatch(item.link);
    return (
      <NavLink
        className={cx(classes.link, {
          [classes.linkActive]: match !== null,
        })}
        to={item.link}
        key={item.label}
        onClick={(event) => {
          event.preventDefault();
          navigate(item.link);
        }}
      >
        <div style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}>
          {item.icon}
        </div>
        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <Navbar 
      width={{ base: 80, sm: 270 }} 
      p="md" 
      sx={theme => ({
        backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
        border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[5] : theme.colors.gray[2]}`,
        transition: 'width 0.3s ease',
      })}
    >
      <Navbar.Section grow>
        <Group className={classes.navHeader} position="center">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.3s ease",
            }}
          >
            <a
              href="https://hmomen.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                display: "flex", 
                justifyContent: "center",
                transition: "transform 0.3s ease",
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
            >
              {logoIcon}
            </a>
            <Text
              sx={(theme) => ({
                fontSize: "22px",
                fontWeight: 600,
                color: theme.colorScheme === "dark" ? theme.white : theme.colors.gray[8],
                textAlign: "center",
                '@media (max-width: 768px)': {
                  display: 'none'
                }
              })}
            >
              إرشاد التائهين
            </Text>
          </div>
        </Group>
        
        <Divider 
          my="md" 
          sx={(theme) => ({
            borderColor: theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2],
            opacity: 0.6,
          })}
        />
        
        <div style={{ marginTop: '20px' }}>
          {data.map((item) => (
            <NavLinkView key={item.link} item={item} />
          ))}
        </div>
      </Navbar.Section>
      
      <Divider 
        my="md" 
        sx={(theme) => ({
          borderColor: theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2],
          opacity: 0.6,
        })}
      />
      
      <div style={{ marginBottom: 12 }}>
        <Divider 
          my="sm" 
          sx={(theme) => ({
            borderColor: theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2],
            opacity: 0.6,
          })}
        />
        <Group position="center" spacing="sm" style={{ marginBottom: 8 }}>
          <Avatar 
            color="green" 
            radius="xl" 
            size="sm"
            sx={{ '@media (max-width: 768px)': { display: 'none' } }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
          <Text 
            size="sm" 
            sx={(theme) => ({
              color: theme.colorScheme === "dark" ? theme.colors.dark[2] : theme.colors.gray[6],
              '@media (max-width: 768px)': { display: 'none' },
            })}
          >
            {displayName}
          </Text>
        </Group>
        <Button
          fullWidth
          variant="subtle"
          color="red"
          size="sm"
          leftIcon={<Logout size={16} />}
          onClick={logout}
          sx={{
            '@media (max-width: 768px)': {
              padding: '8px',
              '.mantine-Button-label': { display: 'none' },
            },
          }}
        >
          تسجيل الخروج
        </Button>
      </div>
      
      <a
        href="https://alkafeel.net/"
        style={{
          textAlign: "center",
          display: "block",
          transition: "transform 0.3s ease",
          '&:hover': {
            transform: 'scale(1.05)',
          }
        }}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img 
          src={AtbaImage} 
          alt="ATBA" 
          style={{
            maxWidth: "100%",
            height: "auto",
            maxHeight: "120px",
            transition: "transform 0.3s ease",
          }} 
        />
      </a>
    </Navbar>
  );
};

export default NavBar;
